import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { Button, Card, Input, Spinner } from "@nutrayan/ui";
import { apiClient } from "@/lib/api-client";

// Contract: GET /admin/api/v1/inventory/variants/{variantId}/availability,
// POST .../adjust (AdjustStockPayload {warehouseId, delta}).

interface InventoryAvailability {
  variantId?: number;
  onHand?: number;
  reserved?: number;
  committed?: number;
  available?: number;
  inStock?: boolean;
}

export default function InventoryPage() {
  const qc = useQueryClient();
  const [variantId, setVariantId] = useState("1");
  const [warehouseId, setWarehouseId] = useState("1");
  const [delta, setDelta] = useState("10");

  const availability = useQuery({
    queryKey: ["admin", "inventory", variantId],
    queryFn: () =>
      apiClient.request<InventoryAvailability>(
        `/admin/api/v1/inventory/variants/${variantId}/availability`,
      ),
    enabled: /^\d+$/.test(variantId),
  });

  const adjust = useMutation({
    mutationFn: () =>
      apiClient.request<InventoryAvailability>(
        `/admin/api/v1/inventory/variants/${variantId}/adjust`,
        { method: "POST", body: { warehouseId: Number(warehouseId), delta: Number(delta) } },
      ),
    onSuccess: () => {
      toast.success("Stock adjusted");
      qc.invalidateQueries({ queryKey: ["admin", "inventory", variantId] });
    },
    onError: (e: Error) => toast.error(e.message || "Adjustment failed"),
  });

  const a = availability.data;

  const cells: Array<[string, number | undefined]> = [
    ["On hand", a?.onHand],
    ["Reserved", a?.reserved],
    ["Committed", a?.committed],
    ["Available", a?.available],
  ];

  return (
    <div className="max-w-3xl space-y-6">
      <header>
        <h1 className="font-display text-display-xl text-charcoal">Inventory</h1>
        <p className="text-body text-ink">
          Per-variant availability across warehouses. (Warehouse list endpoint pending —
          BLK-API-009.)
        </p>
      </header>

      <Card>
        <label className="text-caption text-muted" htmlFor="variant">
          Variant ID
        </label>
        <div className="mt-1 flex max-w-xs gap-2">
          <Input
            id="variant"
            type="number"
            value={variantId}
            onChange={(e) => setVariantId(e.target.value)}
          />
        </div>

        {availability.isPending ? (
          <Spinner className="mt-4" />
        ) : availability.isError ? (
          <p className="text-body text-terracotta mt-4">
            Couldn&apos;t load availability for variant {variantId}.
          </p>
        ) : (
          <div className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-4">
            {cells.map(([label, value]) => (
              <div key={label} className="bg-sand rounded p-3 text-center">
                <p className="text-caption text-muted">{label}</p>
                <p className="font-display text-display-md text-charcoal mt-1">{value ?? "—"}</p>
              </div>
            ))}
          </div>
        )}
        {a ? (
          <p className={`text-body-sm mt-3 ${a.inStock ? "text-forest" : "text-terracotta"}`}>
            {a.inStock ? "✓ In stock" : "✗ Out of stock"}
          </p>
        ) : null}
      </Card>

      <Card>
        <h2 className="font-display text-display-sm text-charcoal">
          Adjust stock (restock / correction)
        </h2>
        <div className="mt-3 flex flex-wrap items-end gap-3">
          <div>
            <label className="text-caption text-muted" htmlFor="warehouse">
              Warehouse ID
            </label>
            <Input
              id="warehouse"
              type="number"
              className="w-32"
              value={warehouseId}
              onChange={(e) => setWarehouseId(e.target.value)}
            />
          </div>
          <div>
            <label className="text-caption text-muted" htmlFor="delta">
              Delta (+/−)
            </label>
            <Input
              id="delta"
              type="number"
              className="w-32"
              value={delta}
              onChange={(e) => setDelta(e.target.value)}
            />
          </div>
          <Button onClick={() => adjust.mutate()} disabled={adjust.isPending}>
            {adjust.isPending ? "Adjusting…" : "Apply"}
          </Button>
        </div>
      </Card>
    </div>
  );
}
